import ResizeObserver from 'resize-observer-polyfill';

const BREAKPOINT_ORDER = {
  xs: 0,
  sm: 1,
  md: 2,
  lg: 3,
  xl: 4,
};

function resolveDefaultWidth (thresholds, device) {
  if (!device) {
    return 0;
  }

  if (device.isTablet) {
    return thresholds.md;
  }

  if (device.isMobile) {
    return thresholds.xs - 1;
  }

  return thresholds.lg;
}

function computeBreakpointFlags (width, thresholds, scrollBarWidth) {
  const xs = width < thresholds.xs;
  const sm = width < thresholds.sm && !xs;
  const md = width <= thresholds.md && !(sm || xs);
  const lg = width < thresholds.lg - scrollBarWidth && !(md || sm || xs);
  const xl = width >= thresholds.lg - scrollBarWidth;

  return { xs, sm, md, lg, xl };
}

function computeAndDownFlags ({ xs, sm, md, lg, xl }) {
  return {
    smAndDown: (xs || sm) && !(md || lg || xl),
    mdAndDown: (xs || sm || md) && !(lg || xl),
    lgAndDown: (xs || sm || md || lg) && !xl,
  };
}

function computeAndUpFlags ({ xs, sm, md, lg, xl }) {
  return {
    smAndUp: !xs && (sm || md || lg || xl),
    mdAndUp: !(xs || sm) && (md || lg || xl),
    lgAndUp: !(xs || sm || md) && (lg || xl),
  };
}

function resolveBreakpointName ({ xs, sm, md, lg }) {
  if (xs) {
    return 'xs';
  }

  if (sm) {
    return 'sm';
  }

  if (md) {
    return 'md';
  }

  if (lg) {
    return 'lg';
  }

  return 'xl';
}

export default class Breakpoint {
  constructor (config, device = null) {
    const {
      mobileBreakpoint,
      scrollBarWidth,
      thresholds,
    } = config;

    this.mobileBreakpoint = mobileBreakpoint;
    this.scrollBarWidth = scrollBarWidth;
    this.thresholds = thresholds;
    this.defaultWidth = resolveDefaultWidth(thresholds, device);

    this.init();
  }

  init () {
    this.update();

    if (typeof window === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(this.update.bind(this));
    observer.observe(document.documentElement);
  }

  update () {
    const height = this.getClientHeight();
    const { width, scrollBarWidth } = this.getClientWidth();

    this.scrollBarWidth = scrollBarWidth;
    this.height = height;
    this.width = width;

    const flags = computeBreakpointFlags(width, this.thresholds, this.scrollBarWidth);

    this.xs = flags.xs;
    this.sm = flags.sm;
    this.md = flags.md;
    this.lg = flags.lg;
    this.xl = flags.xl;

    this.xsOnly = flags.xs;
    this.smOnly = flags.sm;
    this.mdOnly = flags.md;
    this.lgOnly = flags.lg;
    this.xlOnly = flags.xl;

    Object.assign(this, computeAndDownFlags(flags), computeAndUpFlags(flags));

    this.name = resolveBreakpointName(flags);
    this.mobile = this.computeMobile(width);
  }

  computeMobile (width) {
    if (typeof this.mobileBreakpoint === 'number') {
      return width < Number.parseInt(this.mobileBreakpoint, 10);
    }

    const current = BREAKPOINT_ORDER[this.name];
    const max = BREAKPOINT_ORDER[this.mobileBreakpoint];

    return current <= max;
  }

  getClientWidth () {
    let width = this.defaultWidth || 0;
    let scrollBarWidth = this.scrollBarWidth || 0;
    /* istanbul ignore if */
    if (typeof window !== 'undefined') {
      width = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
      scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    }
    return { width, scrollBarWidth };
  }

  getClientHeight () {
    if (typeof document === 'undefined') {
      return 0;
    }
    return Math.max(document.documentElement?.clientHeight, window.innerHeight || 0);
  }
}
